import { useCallback, useContext, useEffect, useState } from 'react';
import { Box, Icon } from '@zillow/constellation-10';
import { useSatelliteWebLandlordLeadManagementHandler_leadManagementSearch } from '@zg-rentals/ts-satellite-client-react-query';
import {
  SatelliteConversationFolderTypeV2,
  type SatelliteWebLandlordLeadManagementSearchEntryDto,
} from '@zg-rentals/ts-satellite-client';
import { getDateRangesInMsFromDaysBack } from '../../utils/getDateRangesInMsFromDaysBack';
import type { SearchResultItem } from '../SearchBar';
import SearchBar, {
  ERROR_MESSAGE,
  filterSpecialCharacters,
  MAX_CHARACTERS_MESSAGE,
  MAX_LENGTH_SEARCH_TEXT,
} from '../SearchBar';
import { LeadManagementContext } from '../../contexts/LeadManagementContextProvider';
import useQueryParamsNext from '../../hooks/useQueryParamsNext';
import getFolderFromQueryParam from '../../utils/getFolderFromQueryParam';
import {
  emitSearchForALeadEvent,
  SEARCH_FOR_A_LEAD_EVENT_TYPE_ID,
} from '../../constants/clickstreamEvents/leadManagementTable';
import getIsPhoneNumber from '../../utils/getIsPhoneNumber';
import getFormattedSearchText from '../../utils/getFormattedSearchText';
import { IconCheckmarkFilled, IconSearchFilled } from '@zillow/constellation-icons';
import EmptyState from '../EmptyState';
import { useAuth } from '../../AuthProvider';
import { rentalClient } from '../../httpClient';

const DEFAULT_DAYS_BACK = 90;
const SEARCH_PAGE_SIZE = 10;

const formatSearchResults = (
  searchResults: Array<SatelliteWebLandlordLeadManagementSearchEntryDto>,
  showRenterPhoneNumber = false,
): Array<SearchResultItem> => {
  return searchResults.map((searchResult) => {
    const { searchEntry, screeningCriteria } = searchResult;
    const { conversationId, folder, listingAlias, renterName, renterPhoneNumber } = searchEntry;
    const {
      criteriaMatchedDisplay,
      numCriteriaSelectedByLandlord,
      numRequiredOrNegotiableCriteriaSelectedByLandlordThatMatch,
    } = screeningCriteria;
    const label = showRenterPhoneNumber ? `${renterName} ${renterPhoneNumber}` : renterName;
    const hasMatchedAllCriteria =
      numRequiredOrNegotiableCriteriaSelectedByLandlordThatMatch !== 0 &&
      numCriteriaSelectedByLandlord !== 0 &&
      numRequiredOrNegotiableCriteriaSelectedByLandlordThatMatch === numCriteriaSelectedByLandlord;

    return {
      meta: criteriaMatchedDisplay,
      icon: hasMatchedAllCriteria ? (
        <Icon css={{ color: 'icon.accent.green.strong' }} render={<IconCheckmarkFilled />} />
      ) : undefined,
      value: renterName,
      label,
      conversationId,
      listingAlias,
      folder,
    };
  });
};

type LeadManagementSearchBarProps = {
  setConversationIdToFilterBy: (conversationId: string | null) => void;
  isDisabled: boolean;
};

export default function LeadManagementSearchBar({
  setConversationIdToFilterBy,
  isDisabled,
}: LeadManagementSearchBarProps) {
  const { listingAliases, source } = useContext(LeadManagementContext);
  const { updateQueryParams } = useQueryParamsNext();
  const { user } = useAuth();

  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [helperText, setHelperText] = useState('');
  const [offset, setOffset] = useState(0);
  const [accumulatedResults, setAccumulatedResults] = useState<Array<SatelliteWebLandlordLeadManagementSearchEntryDto>>(
    [],
  );
  const [hasMoreSearchResults, setHasMoreSearchResults] = useState(false);

  const { startDateMs: startTimestampMs, endDateMs: endTimestampMs } = getDateRangesInMsFromDaysBack(DEFAULT_DAYS_BACK);

  // Strip special characters (parens, dots, plus signs, etc.) before phone detection and API submission.
  // Without this, pasted phone formats like "(206) 555-1234" would (a) fail getIsPhoneNumber's regex
  // (no parens allowed) and (b) hit the API verbatim instead of as "206-555-1234".
  const sanitizedDebouncedSearchText = filterSpecialCharacters(debouncedSearchText);
  const isPhoneNumber = getIsPhoneNumber(sanitizedDebouncedSearchText);
  const formattedSearchText = sanitizedDebouncedSearchText ? getFormattedSearchText(sanitizedDebouncedSearchText) : '';
  const isQueryEnabled = formattedSearchText.length > 1;
  const listingFilterKey = listingAliases.join(',');

  const searchPostData = {
    listingAliases,
    searchText: formattedSearchText,
    source: source ?? undefined,
    offset,
    limit: SEARCH_PAGE_SIZE,
    startTimestampMs,
    endTimestampMs,
  };

  const {
    data: searchData,
    error,
    isFetching,
  } = useSatelliteWebLandlordLeadManagementHandler_leadManagementSearch(searchPostData, {
    httpClient: rentalClient,
    enabled: isQueryEnabled,
    // Match Apollo's behavior: attempt the fetch even when the browser reports offline.
    // Without this, React Query's default networkMode: 'online' pauses the query while
    // offline (no error is surfaced), so the input never enters its error state.
    networkMode: 'always',
    meta: {
      context: {
        api: 'leadManagementSearch',
        details: 'LeadManagementSearchBar - Error fetching lead management search result data',
        listingAliases: listingFilterKey,
        source: source ?? null,
        offset,
        limit: SEARCH_PAGE_SIZE,
        searchTextFormatted: formattedSearchText,
        startTimestampMs,
        endTimestampMs,
      },
    },
  });

  // Debounce search text (300ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchText(searchText), 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    setOffset(0);
    setAccumulatedResults([]);
    setHasMoreSearchResults(false);
  }, [debouncedSearchText, listingFilterKey, source]);

  // Sync accumulated results from the query.
  useEffect(() => {
    if (!searchData || !isQueryEnabled) return;

    if (offset === 0) {
      setAccumulatedResults(searchData.responseEntries ?? []);
    } else {
      setAccumulatedResults((prev) => [...prev, ...(searchData.responseEntries ?? [])]);
    }
    setHasMoreSearchResults(searchData.hasMore ?? false);
  }, [searchData, offset, isQueryEnabled]);

  // Surface query errors via the helper text under the input
  useEffect(() => {
    if (error) {
      setHelperText(ERROR_MESSAGE);
    }
  }, [error]);

  // Also clears conversationIdToFilterBy so the table isn't stuck on a stale conversation
  // from the previous listing or source scope.
  useEffect(() => {
    setSearchText('');
    setDebouncedSearchText('');
    setHelperText('');
    setConversationIdToFilterBy(null);
  }, [listingFilterKey, source, setConversationIdToFilterBy]);

  const clearSearch = () => {
    setConversationIdToFilterBy(null);
    setAccumulatedResults([]);
    setHasMoreSearchResults(false);
  };

  const handleSelectLeadFromSearch = ({ conversationId, folder, listingAlias: itemListingAlias }: SearchResultItem) => {
    updateQueryParams({ queryParamValues: getSelectedSearchResultQueryParams({ conversationId, folder }) });
    setConversationIdToFilterBy(conversationId);
    // The selected result's own alias — the page-level filter can span multiple listings.
    emitSearchForALeadEvent(user, { listingAlias: itemListingAlias });
  };

  const handleChangeInputText = (inputText: string) => {
    const formattedInputText = filterSpecialCharacters(inputText);
    setSearchText(inputText);

    if (helperText) {
      setHelperText('');
    }

    if (inputText.length === MAX_LENGTH_SEARCH_TEXT) {
      setHelperText(MAX_CHARACTERS_MESSAGE);
    }

    if (!formattedInputText) {
      clearSearch();
    }
  };

  const handleFetchMoreSearchResults = useCallback((_searchTextForMore: string, nextOffset: number) => {
    setOffset(nextOffset);
  }, []);

  const formattedSearchResults = formatSearchResults(accumulatedResults, isPhoneNumber);

  // Treat the gap between typing and the debounced query firing as "still searching" so
  // the "No matching leads" heading doesn't flash while the user is typing.
  const isSearchInFlight = searchText !== debouncedSearchText || isFetching;

  const renderEmptyState = useCallback(() => {
    if (!searchText.length) return null;

    return (
      <EmptyState
        icon={<IconSearchFilled />}
        heading={searchText.length > 2 && !isSearchInFlight ? 'No matching leads' : undefined}
        headingLevel={3}
        body="Try searching by name or phone number."
        css={{ paddingInline: 'default', paddingBlock: 'tighter' }}
      />
    );
  }, [isSearchInFlight, searchText]);

  return (
    <Box
      css={{
        // oxlint-disable-next-line constellation/no-raw-spacing-values -- `width: 360px` is past the end of the numeric sizes scale, which stops at `5000` (200px)
        width: '360px',
        maxWidth: '100%',
      }}
    >
      <SearchBar
        key={`${listingFilterKey}|${source ?? ''}`}
        data-eventid={SEARCH_FOR_A_LEAD_EVENT_TYPE_ID}
        isDisabled={isDisabled}
        label="Search leads"
        onSelectSearchItem={handleSelectLeadFromSearch}
        placeholder="Search for a lead"
        renderEmptyState={renderEmptyState}
        searchText={searchText}
        fetchMoreSearchResults={handleFetchMoreSearchResults}
        hasError={!!error}
        hasMoreSearchResults={hasMoreSearchResults}
        helperText={helperText}
        onChangeInputText={handleChangeInputText}
        searchResults={searchText ? formattedSearchResults : []}
      />
    </Box>
  );
}

/**
 * Computes the `queryParamValues` payload `handleSelectLeadFromSearch` passes to
 * `updateQueryParams` when the user clicks a search result. Pulled out as a pure
 * named export so the folder-routing branches are unit-testable without driving
 * the SearchBar combobox dropdown UI.
 *
 * - SPAM / ARCHIVED selections clear `conversationId` (so the user lands on that
 *   folder's list, not the conversation panel).
 * - INBOX selections clear `folder` (so the user lands in inbox-default scope).
 * - Other filters (`renterProgressStatus`, `readRepliedStatus`, `minCriteriaMatches`)
 *   are always cleared so the destination view isn't filtered by carry-over state.
 */
export const getSelectedSearchResultQueryParams = ({
  conversationId,
  folder,
}: Pick<SearchResultItem, 'conversationId' | 'folder'>) => {
  const isSpamOrArchived =
    folder === SatelliteConversationFolderTypeV2.SPAM || folder === SatelliteConversationFolderTypeV2.ARCHIVED;
  const isInbox = folder === SatelliteConversationFolderTypeV2.INBOX;
  return {
    conversationId: isSpamOrArchived ? null : conversationId,
    folder: isInbox ? null : getFolderFromQueryParam(folder),
    renterProgressStatus: null,
    readRepliedStatus: null,
    minCriteriaMatches: null,
  };
};
